import {
  CalendarIcon,
  ChevronDownIcon,
  DownloadIcon,
  RepeatIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Select,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { LocalDate } from "@js-joda/core";
import React from "react";
import DataTable from "../../../../shared/components/DataTable/DataTable";
import useQueryDataTable from "../../../../shared/components/DataTable/useQueryDataTable";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import { useUploadsAiCapabilities } from "./uploads.ai";
import { createFilters } from "../../../../shared/hooks/useFilters";
import useApi from "../../../../shared/hooks/useApi";
import { usePageFetchingObserver } from "../../../../shared/hooks/usePageIndication";
import { components, ProcessJobId } from "../../../../shared/schema/schema";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";
import { auth } from "../../../../core/auth";
import { downloadCsvFileData } from "../../../../shared/utils/csv-utils";
import { ProcessType } from "./uploads.queries";

type ProcessJob = components["schemas"]["ProcessJob"];
type ProcessJobItem = components["schemas"]["ProcessJobItem"];

type FilterState = {
  from: LocalDate;
  to: LocalDate;
};

const initialFilterState: FilterState = {
  from: LocalDate.now().minusMonths(1),
  to: LocalDate.now(),
};

const { createRangeDatePickerFilter } = createFilters<FilterState>();

function getStatusColor(status: ProcessJob["status"]): string {
  switch (status) {
    case "running":
      return "blue";
    case "stopped":
      return "orange";
    case "completed":
      return "green";
    case "error":
      return "red";
  }
}

function getItemStatusColor(status: ProcessJobItem["status"]): string {
  switch (status) {
    case "completed":
      return "green";
    case "error":
      return "red";
    case "cancelled":
      return "orange";
    default:
      return "gray";
  }
}

export default function UploadsListTab() {
  const [processType, setProcessType] = React.useState<ProcessType>("AUTH_FILE");
  const [filters, setFilters] = React.useState<FilterState>(initialFilterState);

  // This tab tells the omnibar what its own filters are, while it is on screen.
  useUploadsAiCapabilities({
    setFilter: (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
  });
  const { queries, api } = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [expandedJobId, setExpandedJobId] = React.useState<ProcessJobId | null>(null);

  const queryOptions = queries.uploads.jobs({
    processType,
    from: filters.from,
    to: filters.to,
  });

  const fromToFilter = createRangeDatePickerFilter({
    label: "Date range",
    startDate: { name: "from", value: filters.from },
    endDate: { name: "to", value: filters.to },
    onChange: (key, value) => {
      if (value !== undefined) {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
    },
  });

  const query = useQuery({
    ...queryOptions,
    select: (data) => data.list,
    refetchInterval: (query) =>
      query.state.data?.list?.some((job) => job.status === "running") ? 5000 : false,
  });

  const stopMutation = useMutation({
    mutationFn: async (jobId: ProcessJobId) => {
      // api.post injects the agency/member path + auth token automatically.
      await api.post("./process_jobs/:jobId/stop", { path: { jobId } });
    },
    onSuccess: () => {
      toast({
        title: "Job stop requested",
        status: "success",
        position: "top-right",
      });
      queryClient.invalidateQueries({ queryKey: ["process_jobs", "jobs"] });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not stop job",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: (jobId: ProcessJobId) =>
      api.get("./process_jobs/:jobId/errors_file", { path: { jobId } }),
    onSuccess: (data) => downloadCsvFileData(data.fileName, data.csv),
    onError: (error: unknown) =>
      toast({
        title: "Could not download errors file",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      }),
  });

  const retryMutation = useMutation({
    mutationFn: (jobId: ProcessJobId) =>
      api.post("./process_jobs/:jobId/retry_errored", { path: { jobId } }),
    onSuccess: (data) => {
      toast({
        title: `Retry upload #${data.jobId} created`,
        status: "success",
        position: "top-right",
      });
      setFilters(initialFilterState);
      queryClient.invalidateQueries({ queryKey: ["process_jobs", "jobs"] });
    },
    onError: (error: unknown) =>
      toast({
        title: "Could not retry errored items",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      }),
  });

  const columns = React.useMemo(
    () =>
      createColumns(
        (jobId) => stopMutation.mutate(jobId),
        stopMutation.isPending,
        (jobId) => setExpandedJobId((prev) => (prev === jobId ? null : jobId)),
        (jobId) => downloadMutation.mutate(jobId),
        (jobId) => retryMutation.mutate(jobId),
        downloadMutation.isPending || retryMutation.isPending
      ),
    [stopMutation, downloadMutation, retryMutation]
  );

  const { dataTableProps } = useQueryDataTable({
    query,
    columns,
    columnVisiblity: {},
  });

  usePageFetchingObserver(queryOptions);

  const actionNode = (
    <Button
      colorScheme="blue"
      isDisabled={query.isFetching}
      leftIcon={<SearchIcon />}
      onClick={() => query.refetch()}
    >
      Search
    </Button>
  );

  const filterNode = (
    <InputGroup width="xs">
      <RangeDatePicker {...fromToFilter} inputProps={{ width: "full" }} />
      <InputRightElement>
        <CalendarIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
      </InputRightElement>
    </InputGroup>
  );

  return (
    <Box>
      <Select
        aria-label="Upload type"
        marginBottom={4}
        maxWidth="xs"
        value={processType}
        onChange={(event) => {
          setProcessType(event.target.value as ProcessType);
          setExpandedJobId(null);
        }}
      >
        <option value="AUTH_FILE">Authorizations</option>
        <option value="AR_NOTES">AR notes</option>
      </Select>
      <DataTable
        {...dataTableProps}
        actionNode={actionNode}
        filterNode={filterNode}
        spacing="tight"
      />

      <Drawer
        isOpen={expandedJobId !== null}
        placement="right"
        size="xl"
        onClose={() => setExpandedJobId(null)}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Upload Items (Upload #{expandedJobId})</DrawerHeader>
          <DrawerBody>
            {expandedJobId !== null && <ProcessJobItems jobId={expandedJobId} />}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

// Rendered only while a job is selected, so `jobId` is always a real
// ProcessJobId — the items query needs no placeholder id or `enabled` guard.
function ProcessJobItems({ jobId }: { jobId: ProcessJobId }) {
  const { queries } = useApi();
  const jobItemsQuery = useQuery({
    ...queries.uploads.jobItems(jobId),
    select: (data) => data.list,
  });

  if (jobItemsQuery.isLoading) {
    return <Text>Loading items...</Text>;
  }
  if (jobItemsQuery.isError || jobItemsQuery.data === undefined) {
    return <Text color="red.500">Failed to load items</Text>;
  }

  return (
    // Fixed layout with explicit widths: item errors are full sentences, and
    // with auto layout the content-sized columns push the Error column past
    // the edge of the drawer.
    <Table size="sm" sx={{ tableLayout: "fixed" }} variant="simple">
      <Thead>
        <Tr>
          <Th width="8%">ID</Th>
          <Th width="16%">Item</Th>
          <Th width="20%">Details</Th>
          <Th width="20%">Result</Th>
          <Th width="11%">Status</Th>
          <Th width="25%">Error</Th>
        </Tr>
      </Thead>
      <Tbody>
        {jobItemsQuery.data.map((item) => (
          <Tr key={item.id}>
            <Td>{item.id}</Td>
            <Td>{item.label}</Td>
            <Td>{item.detail}</Td>
            <Td>{item.resultText}</Td>
            <Td>
              <Badge colorScheme={getItemStatusColor(item.status)}>{item.status}</Badge>
            </Td>
            <Td>
              {item.errorMessage && (
                <Text color="red.500" fontSize="xs">
                  {item.errorMessage}
                </Text>
              )}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

const { accessor, display } = createColumnHelper<ProcessJob>();

const createColumns = (
  onStop: (jobId: ProcessJobId) => void,
  isStopping: boolean,
  onViewItems: (jobId: ProcessJobId) => void,
  onDownloadErrors: (jobId: ProcessJobId) => void,
  onRetryErrors: (jobId: ProcessJobId) => void,
  isActing: boolean
) => [
  accessor("id", {
    header: "ID",
    sortingFn: "basic",
  }),
  accessor("label", {
    header: "Type",
    sortingFn: "alphanumeric",
  }),
  display({
    id: "status",
    cell: (x) => (
      <Badge colorScheme={getStatusColor(x.row.original.status)}>{x.row.original.status}</Badge>
    ),
    header: "Status",
  }),
  accessor("createdAt", {
    header: "Created At",
    // The built-in "alphanumeric" sorter coerces the Instant object to "" (so
    // every row compares equal) — compare the Instants directly instead.
    sortingFn: (rowA, rowB) => rowA.original.createdAt.compareTo(rowB.original.createdAt),
    cell: (x) => dateFormatter.toDateTime(x.row.original.createdAt),
  }),
  accessor("createdByName", {
    header: "Created By",
    sortingFn: "alphanumeric",
  }),
  accessor("fileName", {
    header: "File Name",
    sortingFn: "alphanumeric",
  }),
  display({
    id: "progress",
    cell: (x) => {
      const { completedItems, totalItems, errorItems, cancelledItems, pendingItems } =
        x.row.original;
      return (
        <Flex direction="column" fontSize="sm" gap={0}>
          <Text>
            {completedItems}/{totalItems} completed
          </Text>
          {errorItems > 0 && <Text color="red.500">{errorItems} errored</Text>}
          {cancelledItems > 0 && <Text color="orange.500">{cancelledItems} cancelled</Text>}
          {pendingItems > 0 && <Text color="blue.500">{pendingItems} pending</Text>}
        </Flex>
      );
    },
    header: "Progress",
  }),
  display({
    id: "errors",
    cell: (x) =>
      x.row.original.errorMessage ? (
        <Text color="red.500" fontSize="sm">
          {x.row.original.errorMessage}
        </Text>
      ) : null,
    header: "Errors",
  }),
  display({
    id: "actions",
    cell: (x) => (
      <Flex gap={2}>
        <Menu placement="bottom-end" strategy="fixed">
          <MenuButton
            as={Button}
            isDisabled={isActing || x.row.original.errorItems === 0}
            rightIcon={<ChevronDownIcon />}
            size="sm"
            variant="outline"
          >
            Other actions
          </MenuButton>
          <Portal>
            <MenuList>
              <MenuItem icon={<DownloadIcon />} onClick={() => onDownloadErrors(x.row.original.id)}>
                Download errors file
              </MenuItem>
              <MenuItem
                icon={<RepeatIcon />}
                isDisabled={
                  !auth.isAllowedTo(
                    x.row.original.processType === "AR_NOTES"
                      ? "view_billing_section_ar"
                      : "upload_authorizations"
                  )
                }
                onClick={() => onRetryErrors(x.row.original.id)}
              >
                Retry errored
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
        <Button
          colorScheme="blue"
          size="sm"
          variant="outline"
          onClick={() => onViewItems(x.row.original.id)}
        >
          View
        </Button>
        {x.row.original.status === "running" && (
          <Button
            colorScheme="red"
            isDisabled={
              isStopping ||
              !auth.isAllowedTo(
                x.row.original.processType === "AR_NOTES"
                  ? "view_billing_section_ar"
                  : "upload_authorizations"
              )
            }
            size="sm"
            onClick={() => onStop(x.row.original.id)}
          >
            Stop
          </Button>
        )}
      </Flex>
    ),
    header: "Actions",
  }),
];
